import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Designation } from '../../types';
import { apiClient } from '../../api/apiClient';

interface DesignationState {
  designations: Designation[];
  loading: boolean;
  error: string | null;
}

const initialState: DesignationState = {
  designations: [],
  loading: false,
  error: null,
};

export const fetchDesignationsAsync = createAsyncThunk(
  'designations/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/designations');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch designations');
    }
  }
);

export const addDesignationAsync = createAsyncThunk(
  'designations/add',
  async (designation: Partial<Designation>, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/designations', designation);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add designation');
    }
  }
);

export const updateDesignationAsync = createAsyncThunk(
  'designations/update',
  async (designation: Partial<Designation>, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/designations/${designation.id || designation._id}`, designation);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update designation');
    }
  }
);

export const deleteDesignationAsync = createAsyncThunk(
  'designations/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/designations/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete designation');
    }
  }
);

const designationSlice = createSlice({
  name: 'designations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDesignationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesignationsAsync.fulfilled, (state, action: PayloadAction<Designation[]>) => {
        state.loading = false;
        state.designations = action.payload;
      })
      .addCase(fetchDesignationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addDesignationAsync.fulfilled, (state, action: PayloadAction<Designation>) => {
        state.designations.push(action.payload);
      })
      .addCase(updateDesignationAsync.fulfilled, (state, action: PayloadAction<Designation>) => {
        const index = state.designations.findIndex((d) => d.id === action.payload.id || d._id === action.payload._id);
        if (index !== -1) {
          state.designations[index] = action.payload;
        }
      })
      .addCase(deleteDesignationAsync.fulfilled, (state, action: PayloadAction<string>) => {
        state.designations = state.designations.filter((d) => d.id !== action.payload && d._id !== action.payload);
      });
  },
});

export default designationSlice.reducer;
